import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../api/api.service';
import { useNotification } from '../shared/NotificationSystem';
import CloseIcon from '../icons/CloseIcon';
import styles from './UserModal.module.scss';

interface AddUserModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreated: () => void;
    currentLanguage: string;
}

const AddUserModal: React.FC<AddUserModalProps> = ({ isOpen, onClose, onCreated, currentLanguage }) => {
    const { t } = useTranslation();
    const { showSuccess, showError } = useNotification();

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [expirationDate, setExpirationDate] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const validate = (): boolean => {
        if (!name.trim()) {
            showError('statistics.addUser.nameRequired');
            return false;
        }
        const emailRegex = /[^@\s]+@[^@\s]+\.[^@\s]+/;
        if (!emailRegex.test(email)) {
            showError('statistics.addUser.invalidEmail');
            return false;
        }
        if (!expirationDate) {
            showError('statistics.addUser.expirationRequired');
            return false;
        }
        return true;
    };

    const handleCreate = async () => {
        if (!validate()) return;
        setIsLoading(true);
        try {
            const absoluteDateTime = `${expirationDate}T23:59:59.999Z`;
            await apiService.createUser({
                email,
                name,
                language: currentLanguage,
                subscription_expiration_date: absoluteDateTime
            });
            showSuccess('statistics.addUser.createSuccess');
            onCreated();
            onClose();
            setName('');
            setEmail('');
            setExpirationDate('');
        } catch (error) {
            console.error('Create user error:', error);
            showError('statistics.addUser.createError');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOverlayClick = () => {
        if (!isLoading) onClose();
    };

    return (
        <div className={styles.modalOverlay} onClick={handleOverlayClick}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>{t('statistics.addUser.title')}</h2>
                    <button className={styles.closeButton} onClick={onClose} disabled={isLoading}>
                        <CloseIcon />
                    </button>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.userInfoGrid}>
                        <div className={styles.infoItem}>
                            <label>{t('statistics.userName')}:</label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={t('statistics.addUser.namePlaceholder')}
                                className={styles.dateInput}
                            />
                        </div>

                        <div className={styles.infoItem}>
                            <label>{t('statistics.email')}:</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={t('statistics.addUser.emailPlaceholder')}
                                className={styles.dateInput}
                            />
                        </div>

                        <div className={styles.infoItem}>
                            <label>{t('statistics.subscriptionEndDate')}:</label>
                            <input
                                type="date"
                                value={expirationDate}
                                onChange={(e) => setExpirationDate(e.target.value)}
                                placeholder={t('statistics.datePlaceholder')}
                                title={t('statistics.datePlaceholder')}
                                lang={currentLanguage}
                                className={styles.dateInput}
                            />
                        </div>
                    </div>

                    <div className={styles.editButtons}>
                        <button onClick={handleCreate} disabled={isLoading} className={styles.saveButton}>
                            {isLoading ? t('common.loading') : t('statistics.addUser.create')}
                        </button>
                        <button onClick={onClose} className={styles.cancelButton} disabled={isLoading}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddUserModal;


